import React, { useState } from "react";
import { Button, Input } from "reactstrap";
import notifications from "../../../common/Components/Notifications/Notification";
import { changePunchTime, savePunch, switchWork, voidPunch } from "../../../services/karkhanaApi";
import { rupees } from "../rates/rateModel";
import { enqueue, newRequestId } from "../rounds/entryQueue";
import PersonPicker, { Person } from "../rounds/PersonPicker";
import VoiceNote from "../voice/VoiceNote";
import { Activity, OUT_REASONS, fixMessage, hours, linesByActivity, nowHHMM, reasonLabel, stateOf, timeline, workLabel } from "./punchModel";
import { payWorking } from "../rates/timeModel";
import { useKT } from "../i18n/kt";
import { askReason } from "../ui/dialog";
import { SELF_NOTE } from "./attendanceRows";
import TimeField from "../ui/TimeField";

type Mode = "view" | "in" | "out" | "switch";

/** Why a punch is undone: practice entries while supervisors learn, and the usual slips. */
export const UNDO_REASONS = ["Practice / training entry", "Wrong person", "Wrong time", "Marked twice", "Didn't come"];

/** null = leave it to the server: the person's last work, else from their category. */
type WorkChoice = { payBasis: "PIECE" | "TIME"; activityId?: string } | null;

const WorkPicker = ({
  value,
  onChange,
  activities,
  allowUsual,
}: {
  value: WorkChoice;
  onChange: (w: WorkChoice) => void;
  activities: Activity[];
  allowUsual: boolean;
}) => {
  const { t } = useKT();
  return (
  <div className="kr-chips" role="radiogroup" aria-label="Work">
    {allowUsual ? (
      <button type="button" role="radio" aria-checked={value === null} className={`kr-choice${value === null ? " is-on" : ""}`} onClick={() => onChange(null)}>
        {t("Usual work")}
      </button>
    ) : null}
    <button
      type="button"
      role="radio"
      aria-checked={value?.payBasis === "PIECE"}
      className={`kr-choice${value?.payBasis === "PIECE" ? " is-on" : ""}`}
      onClick={() => onChange({ payBasis: "PIECE" })}
    >
      {t("Loom work")}
    </button>
    {activities.map((a) => {
      const on = value?.payBasis === "TIME" && value.activityId === a.id;
      return (
        <button key={a.id} type="button" role="radio" aria-checked={on} className={`kr-choice${on ? " is-on" : ""}`} onClick={() => onChange({ payBasis: "TIME", activityId: a.id })}>
          {t(a.name)}
        </button>
      );
    })}
  </div>
);
};

export type ForLoom = { id: string; code: string; sareeNumber?: string | null };

/** "L:<loomId>" or "S:<sareeNumber>" → the request fields; "" → nothing. */
export const forFields = (value: string): Record<string, string> =>
  value.startsWith("L:") ? { forLoomId: value.slice(2) } : value.startsWith("S:") ? { forSareeNumber: value.slice(2) } : {};

/** Time work can say which loom (warping its taani) or saree (dyeing its yarn) it was for. Optional. */
const ForPicker = ({ value, onChange, looms }: { value: string; onChange: (v: string) => void; looms: ForLoom[] }) => {
  const { t } = useKT();
  if (!looms.length) return null;
  const sarees = looms.filter((l) => l.sareeNumber);
  return (
    <>
      <label className="kr-label" htmlFor="kp-for">
        {t("For (optional)")}
      </label>
      <Input id="kp-for" type="select" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{t("Not for one loom or saree")}</option>
        {sarees.length ? (
          <optgroup label={t("Saree on the loom")}>
            {sarees.map((l) => (
              <option key={`S:${l.sareeNumber}`} value={`S:${l.sareeNumber}`}>
                {t("Saree {{n}} on {{code}}", { n: l.sareeNumber, code: l.code })}
              </option>
            ))}
          </optgroup>
        ) : null}
        <optgroup label={t("Loom (e.g. warping its taani)")}>
          {looms.map((l) => (
            <option key={`L:${l.id}`} value={`L:${l.id}`}>
              {l.code}
            </option>
          ))}
        </optgroup>
      </Input>
    </>
  );
};

/**
 * One person's day (or, with no person, marking someone's arrival). The server checks every punch
 * fits the day; without signal the punch is kept on the phone with the time it was tapped.
 */
const PunchSheet = ({
  person,
  date,
  isToday,
  late,
  activities,
  queueKey,
  roster,
  looms = [],
  onClose,
  onSaved,
  asPage = false,
  self = false,
}: {
  /** Full page (Attendance) instead of a panel over the list. */
  asPage?: boolean;
  /** The signed-in supervisor's own day: shown, never marked or undone by them. */
  self?: boolean;
  person: any | null;
  date: string;
  isToday: boolean;
  late: boolean;
  activities: Activity[];
  queueKey: string;
  roster: Person[];
  looms?: ForLoom[];
  onClose: () => void;
  onSaved: (opts: { queued?: boolean; keepOpen?: boolean }) => void;
}) => {
  const { t } = useKT();
  const state = person ? stateOf(person) : "OUT";
  const [mode, setMode] = useState<Mode>(person ? "view" : "in");
  const [who, setWho] = useState<Person | null>(null);
  const [work, setWork] = useState<WorkChoice>(null);
  const [forWhat, setForWhat] = useState("");
  // An Out late in the day (or one added later to fix a past day) is usually going home, not lunch.
  const defaultOutReason = () => (!isToday || new Date().getHours() >= 15 ? "HOME" : "LUNCH");
  const [reason, setReason] = useState(defaultOutReason);
  const [time, setTime] = useState(isToday ? nowHHMM() : "");
  const [busy, setBusy] = useState(false);
  const [voiceNote, setVoiceNote] = useState<string | null>(null);
  // A saved punch whose time is being changed, and the new time.
  const [editing, setEditing] = useState<{ id: string; time: string } | null>(null);

  const personId: string | undefined = person?.personId || who?.id;
  const shownPunches: any[] = (person?.punches || []).filter((p: any) => p.status !== "VOID");
  // What they're doing now; on an earlier day with a missing Out, the last stretch of that day.
  const stretchesShown: any[] = person?.stretches || [];
  const nowOn = person?.current || stretchesShown[stretchesShown.length - 1] || null;
  const lastPunchIsIn = state === "FIX" && shownPunches.length > 0 && shownPunches[shownPunches.length - 1].type === "IN";
  const personCode: string = person?.code || who?.code || "";

  const send = async (kind: "punch" | "switch", body: Record<string, unknown>, next = false) => {
    if (!personId) {
      notifications({ title: t("Choose the person"), message: t("Search by code or name"), type: "warning" });
      return;
    }
    if (!time) {
      notifications({ title: t("Enter the time"), message: t("When did this happen?"), type: "warning" });
      return;
    }
    const clientRequestId = newRequestId();
    const full = { ...body, personId, date, time, clientRequestId, ...(late && voiceNote ? { voiceNoteUrl: voiceNote } : {}) };
    setBusy(true);
    try {
      let res: any;
      try {
        res = kind === "switch" ? await switchWork(full) : await savePunch(full);
      } catch {
        // No signal: keep it with the time it was tapped and send it later.
        enqueue(queueKey, { clientRequestId, body: { ...full, kind }, loomCode: personCode, queuedAt: new Date().toISOString() });
        notifications({ title: t("Saved on this phone"), message: t("{{code}} is sent when the signal is back", { code: personCode }), type: "info" });
        onSaved({ queued: true, keepOpen: next });
        if (next) reset();
        return;
      }
      if (!res?.success) {
        notifications({ title: t("Not saved"), message: t(res?.message || "Could not save"), type: "danger" });
        return;
      }
      onSaved({ keepOpen: next });
      if (next) reset();
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setWho(null);
    setWork(null);
    setForWhat("");
    setTime(isToday ? nowHHMM() : "");
  };

  const undo = async (p: any) => {
    const why = await askReason({
      title: t("Why is the {{what}} at {{time}} being removed?", { what: p.type === "IN" ? t("In") : t("Out"), time: p.time }),
      choices: UNDO_REASONS,
    });
    if (why === null) return;
    if (!why.trim()) {
      notifications({ title: t("Say why"), message: t("A short reason is kept with the change"), type: "warning" });
      return;
    }
    setBusy(true);
    try {
      const res: any = await voidPunch(p.id, { note: why.trim() });
      if (!res?.success) {
        notifications({ title: t("Not removed"), message: t(res?.message || "Could not remove"), type: "danger" });
        return;
      }
      onSaved({ keepOpen: true });
    } catch {
      notifications({ title: t("No connection"), message: t("Undo needs signal. Try again in a moment."), type: "warning" });
    } finally {
      setBusy(false);
    }
  };

  const saveTime = async () => {
    if (!editing) return;
    if (!editing.time) {
      notifications({ title: t("Enter the time"), message: t("When did this happen?"), type: "warning" });
      return;
    }
    setBusy(true);
    try {
      const res: any = await changePunchTime(editing.id, { time: editing.time });
      if (!res?.success) {
        notifications({ title: t("Not changed"), message: t(res?.message || "Could not change the time"), type: "danger" });
        return;
      }
      setEditing(null);
      onSaved({ keepOpen: true });
    } catch {
      notifications({ title: t("No connection"), message: t("Changing a time needs signal. Try again in a moment."), type: "warning" });
    } finally {
      setBusy(false);
    }
  };

  const timeField = (
    <>
      <span className="kr-label">
        {t("Time")}
      </span>
      <div className="kr-chips" style={{ alignItems: "center" }}>
        <TimeField value={time} onChange={setTime} label={t("Time")} />
        {isToday ? (
          <button type="button" className="kr-choice" onClick={() => setTime(nowHHMM())}>
            {t("Now")}
          </button>
        ) : null}
      </div>
    </>
  );

  const pay = person?.pay;
  const title = person ? person.code : t("Mark arrival");

  return (
    <>
      {asPage ? null : <div className="kr-sheet-backdrop" onClick={onClose} />}
      <section
        className={`kr-sheet${asPage ? " is-page" : ""}`}
        role={asPage ? "region" : "dialog"}
        aria-modal={asPage ? undefined : true}
        aria-label={person ? `${person.code} attendance` : t("Mark arrival")}
      >
        <header className="kr-sheet__head">
          <h3>{title}</h3>
          {person ? <span className="kw-muted kp-head-name">{person.name}</span> : null}
          {asPage ? null : (
            <button type="button" className="kr-iconbtn" onClick={onClose} aria-label="Close">
              ✕
            </button>
          )}
        </header>

        <div className="kr-sheet__body">
          {late ? <div className="kr-banner is-warn">{t("This day is 3 or more days back. Punches wait for the admin's approval.")}</div> : null}
          {late && mode !== "view" ? <VoiceNote value={voiceNote} onChange={setVoiceNote} /> : null}

          {!person ? (
            <>
              <label className="kr-label">{t("Who came in")}</label>
              <PersonPicker value={who} onChange={setWho} placeholder={t("Code or name, e.g. J12")} label="Person" roster={roster} />
            </>
          ) : null}

          {person && mode === "view" ? (
            <>
              {state === "FIX" ? <div className="kr-banner is-warn">{fixMessage(person.flags)}</div> : null}
              {person.waiting ? <div className="kr-banner is-info">{t("Some punches on this day wait for the admin's approval.")}</div> : null}
              <ol className="kp-timeline">
                {timeline(person.punches).map(({ punch: p, switched }) => (
                  <li key={p.id} className={`kp-punch is-${p.type}${editing && editing.id === p.id ? " is-editing" : ""}`}>
                    {editing && editing.id === p.id ? (
                      <span className="kp-punch__edit">
                        <TimeField value={editing.time} onChange={(v) => setEditing({ id: p.id, time: v })} label={t("New time")} autoFocus />
                        <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={saveTime}>
                          {t("Save")}
                        </button>
                        <button type="button" className="btn btn-link btn-sm p-0" disabled={busy} onClick={() => setEditing(null)}>
                          {t("Cancel")}
                        </button>
                      </span>
                    ) : self || switched || p.reason === "SWITCH" ? (
                      <span className="kp-punch__time">{p.time}</span>
                    ) : (
                      // Tap the time to change it (they came at 8, the In was tapped at 10).
                      <button
                        type="button"
                        className="kp-punch__time kp-punch__time--edit"
                        disabled={busy}
                        onClick={() => setEditing({ id: p.id, time: p.time })}
                        aria-label={t("Change the {{what}} time {{time}}", { what: p.type === "IN" ? t("In") : t("Out"), time: p.time })}
                      >
                        {p.time}
                      </button>
                    )}
                    <span className="kp-punch__what">
                      {p.type === "IN" ? (
                        <>
                          {switched ? t("Switched to") : `${t("In")} ·`} {t(workLabel(p.payBasis, activities.find((a) => a.id === p.activityId)))}
                        </>
                      ) : (
                        <>{t("Out")} · {t(reasonLabel(p.reason))}</>
                      )}
                      {p.status === "PENDING_APPROVAL" ? <span className="kw-chip is-review">{t("Waiting")}</span> : null}
                      {p.changedFrom ? <small className="kw-muted"> · {t("was {{time}}", { time: p.changedFrom })}</small> : null}
                    </span>
                    {self ? null : (
                      <button type="button" className="btn btn-link btn-sm p-0" disabled={busy} onClick={() => undo(p)}>
                        {t("Undo")}
                      </button>
                    )}
                  </li>
                ))}
              </ol>

              <div className={`kr-pay${pay?.status === "RATE_NOT_CONFIGURED" ? " is-missing" : ""}`} aria-live="polite">
                <div className="kr-pay__row">
                  <span>{t("Time work")}</span>
                  <span>{hours(person.timeMinutes)}</span>
                </div>
                {person.pieceMinutes ? (
                  <div className="kr-pay__row">
                    <span>{t("Loom work (paid by the inch)")}</span>
                    <span>{hours(person.pieceMinutes)}</span>
                  </div>
                ) : null}
                {linesByActivity(pay?.lines).map((l: any, i: number) => (
                  <div className="kr-pay__row kw-muted" key={i}>
                    <span>
                      {t(l.activity?.name || "Time work")} · {hours(l.minutes)}
                      {l.overtimeMinutes ? ` (${hours(l.overtimeMinutes)} overtime)` : ""}
                    </span>
                    <span>{l.amount ? rupees(l.amount) : t("No rate")}</span>
                    {l.amount && !l.overtimeMinutes && payWorking(l.minutes, l.hourlyRate, l.minuteRate) ? (
                      // How the money was worked out: whole hours at the hour rate, the rest at the minute rate.
                      <small className="kr-pay__working">{payWorking(l.minutes, l.hourlyRate, l.minuteRate)}</small>
                    ) : null}
                  </div>
                ))}
                <div className="kr-pay__row kr-pay__total">
                  <span>{person.stillIn ? t("Time pay so far") : t("Time pay")}</span>
                  <span>
                    {pay?.status === "HELD"
                      ? t("Held until fixed")
                      : pay?.status === "RATE_NOT_CONFIGURED"
                      ? t("No time rate set")
                      : rupees(pay?.total ?? 0)}
                  </span>
                </div>
              </div>
            </>
          ) : null}

          {mode === "in" ? (
            <>
              <label className="kr-label">{t("Work")}</label>
              <WorkPicker value={work} onChange={setWork} activities={activities} allowUsual />
              {work?.payBasis === "TIME" ? <ForPicker value={forWhat} onChange={setForWhat} looms={looms} /> : null}
              {timeField}
            </>
          ) : null}

          {mode === "out" ? (
            <>
              <label className="kr-label">{t("Going out for")}</label>
              <div className="kr-chips" role="radiogroup" aria-label="Reason">
                {OUT_REASONS.map((r) => (
                  <button key={r.key} type="button" role="radio" aria-checked={reason === r.key} className={`kr-choice${reason === r.key ? " is-on" : ""}`} onClick={() => setReason(r.key)}>
                    {t(r.label)}
                  </button>
                ))}
              </div>
              <p className="kw-muted mt-2">{t("Time out is not paid. Mark them In when they are back.")}</p>
              {timeField}
            </>
          ) : null}

          {mode === "switch" ? (
            <>
              <p className="kw-muted">
                {t("Now on {{work}} since {{time}}. Moving to:", { work: t(workLabel(nowOn?.payBasis, nowOn?.activity)), time: nowOn?.since ?? nowOn?.from })}
              </p>
              <WorkPicker value={work} onChange={setWork} activities={activities} allowUsual={false} />
              {work?.payBasis === "TIME" ? <ForPicker value={forWhat} onChange={setForWhat} looms={looms} /> : null}
              {timeField}
            </>
          ) : null}
        </div>

        <footer className="kr-sheet__foot">
          {self ? (
            <p className="kr-self-note" role="note">
              {t(SELF_NOTE)}
            </p>
          ) : mode === "view" ? (
            state === "IN" ? (
              <>
                <Button outline disabled={busy} onClick={() => { setWork(null); setTime(isToday ? nowHHMM() : ""); setMode("switch"); }}>
                  {t("Switch work")}
                </Button>
                <Button className="ResetBtn" disabled={busy} onClick={() => { setTime(isToday ? nowHHMM() : ""); setReason(defaultOutReason()); setMode("out"); }}>
                  {t("Out")}
                </Button>
              </>
            ) : (
              <>
                {state === "FIX" && !isToday ? (
                  <Button outline disabled={busy} onClick={() => { setTime(""); setReason(defaultOutReason()); setMode("out"); }}>
                    {t("Add Out")}
                  </Button>
                ) : null}
                {/* Still in from an earlier In (no Out yet): another In is refused, so offer a work switch. */}
                {lastPunchIsIn ? (
                  <Button className="ResetBtn" disabled={busy} onClick={() => { setWork(null); setTime(isToday ? nowHHMM() : ""); setMode("switch"); }}>
                    {t("Switch work")}
                  </Button>
                ) : (
                  <Button className="ResetBtn" disabled={busy} onClick={() => { setWork(null); setTime(isToday ? nowHHMM() : ""); setMode("in"); }}>
                    {state === "FIX" ? t("Add In") : t("In again")}
                  </Button>
                )}
              </>
            )
          ) : (
            <>
              {person ? (
                <Button outline disabled={busy} onClick={() => setMode("view")}>
                  {t("Back")}
                </Button>
              ) : (
                <Button outline disabled={busy || !who} onClick={() => send("punch", { type: "IN", ...(work || {}), ...(work?.payBasis === "TIME" ? forFields(forWhat) : {}) }, true)}>
                  {t("In · next person")}
                </Button>
              )}
              <Button
                className="ResetBtn"
                disabled={busy || (!person && !who) || (mode === "switch" && !work)}
                onClick={() =>
                  mode === "out"
                    ? send("punch", { type: "OUT", reason })
                    : mode === "switch"
                    ? send("switch", { ...(work || {}), ...(work?.payBasis === "TIME" ? forFields(forWhat) : {}) })
                    : send("punch", { type: "IN", ...(work || {}), ...(work?.payBasis === "TIME" ? forFields(forWhat) : {}) })
                }
              >
                {busy ? t("Saving…") : mode === "out" ? t("Mark Out") : mode === "switch" ? t("Switch") : t("Mark In")}
              </Button>
            </>
          )}
        </footer>
      </section>
    </>
  );
};

export default PunchSheet;
